<script lang="ts">
  import { Shell, Icon, type PortalLink, SignIn } from "@steyncd/steyn-design";
  import History from "./routes/History.svelte";
  import Questions from "./routes/Questions.svelte";

  let section = $state<"questions" | "history">("questions");
  import { session } from "./lib/stores.svelte";
  import { glance } from "./lib/glance.svelte";
  import { attention } from "./lib/attention.svelte";
  import { signIn, signOut, atLeast, completeRedirect, explainAuthError } from "./lib/auth";

  const FRONT_DOOR = import.meta.env.VITE_FRONTDOOR_URL ?? "https://steyn-frontdoor.web.app";
  const FABRIC_API = import.meta.env.VITE_FABRIC_API_URL ?? "";

  let signingIn = $state(false);
  let signInError = $state<string | null>(null);

  /**
   * Finish a redirect sign-in before deciding what to render. Without this the
   * credential in the URL is never collected and the gate simply reappears —
   * the "I click it and nothing happens" loop.
   */
  let settling = $state(true);
  $effect(() => {
    void completeRedirect()
      .then((r) => {
        // A redirect that came back with a real error used to be swallowed, so the gate
        // just reappeared with nothing saying why. Surface it like any other failure.
        if (r.error) signInError = explainAuthError(r.error);
      })
      .finally(() => (settling = false));
  });

  async function doSignIn() {
    signingIn = true;
    signInError = null;
    try {
      await signIn();
    } catch (e) {
      // Null for a cancelled popup: closing a window is a decision, not a fault.
      signInError = explainAuthError(e);
    } finally {
      signingIn = false;
    }
  }

  const ICON: Record<string, string> = {
    ha: "house", hq: "finance", screening: "film", codebots: "bot",
    vault: "vault", homestead: "homestead", waypoint: "waypoint",
    hindsight: "hindsight", fabric: "fabric", frontdoor: "door",
  };

  /**
   * The cross-portal switcher reads Fabric's registry, which is already filtered
   * to what the caller may see. Hindsight itself is dropped — a link to the page
   * you are on is furniture. Any failure leaves the switcher unrendered rather
   * than breaking the page.
   */
  async function loadPortals(): Promise<PortalLink[]> {
    if (!FABRIC_API) return [];
    const { getIdToken } = await import("./lib/auth");
    const token = await getIdToken();
    if (!token) return [];
    const res = await fetch(`${FABRIC_API}/portals`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return [];
    const body = (await res.json()) as { portals: { id: string; name: string; url: string }[] };
    return body.portals
      .filter((p) => p.id !== "hindsight")
      .map((p) => ({ id: p.id, name: p.name, url: p.url, icon: ICON[p.id] ?? "door" }));
  }
</script>

{#if !session.ready || settling}
  <!-- Deliberately blank. Flashing the sign-in card and then replacing it with
       the app is worse than 200ms of nothing. -->
  <div class="boot" aria-busy="true"></div>
{:else if !session.user}
  <SignIn
    glance={glance.stats}
    product="Hindsight"
    mark="hindsight"
    tagline="The house's long memory — what changed, and how it usually behaves."
    onsignin={doSignIn}
    busy={signingIn}
    error={signInError}
    siblings={["Home", "Finance", "Screening Room", "Vault", "Hindsight", "Codebots"]}
  />
{:else if !atLeast(session.claims?.role ?? null, "partner")}
  <!-- Household telemetry says when the house is empty and when people come and
       go. A lounge display has no business with it, and a guest certainly not. -->
  <div class="gate">
    <div class="card--hero gate__card">
      <h1>Hindsight</h1>
      <p class="sub">
        {#if session.claims}
          This portal needs partner access. You are signed in as {session.claims.role}.
        {:else}
          This Google account has no access to the Steyn household.
        {/if}
      </p>
      <a class="btn-primary gate__btn" href={FRONT_DOOR}>Go to the Front Door</a>
      <button class="gate__link" onclick={signOut}>Sign out</button>
    </div>
  </div>
{:else}
  <Shell
    product="Hindsight"
    mark="hindsight"
    portals={loadPortals}
    nav={[
      { id: "questions", label: "Questions", icon: "search" },
      { id: "history", label: "History", icon: "hindsight" },
    ]}
    active={section}
    onnavigate={(id) => (section = id as typeof section)}
    frontDoorUrl={FRONT_DOOR}
  >
    {#snippet header()}
      <!-- Something wants you, wherever you are standing. A number and a link, not a
           notification centre — the Front Door owns the list. -->
      {#if attention.count > 0}
        <a class="wants" href={attention.href} title="{attention.count} thing(s) want you">
          <Icon name="inbox" size={14} />
          <span class="num">{attention.count}</span>
        </a>
      {/if}
      <span class="who">
        <Icon name="user" size={14} />
        {session.user?.displayName ?? session.user?.email}
      </span>
      <button class="signout" onclick={signOut} aria-label="Sign out">
        <Icon name="logout" size={16} />
      </button>
    {/snippet}

    <!-- Questions first: History is the database viewer, and landing on it is
         what made this portal feel like raw data with no way in. -->
    {#if section === "questions"}
      <Questions />
    {:else}
      <History />
    {/if}
  </Shell>
{/if}

<style>
  /* Amber because it wants attention, with a glyph and a number beside it — colour
     is never the only signal. */
  .wants {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 9px;
    border-radius: var(--r-pill);
    font-size: var(--fs-micro);
    font-weight: 800;
    color: var(--warn);
    box-shadow: inset 0 0 0 1px var(--line);
    flex-shrink: 0;
  }
  .wants:hover {
    box-shadow: inset 0 0 0 1px var(--warn);
  }

  .boot { min-height: 100vh; background: var(--aurora); }
  .gate { min-height: 100vh; background: var(--aurora); display: grid; place-items: center; padding: 24px; }
  .gate__card { width: min(380px, 100%); padding: 30px 28px; text-align: center; }
  h1 { margin: 0 0 6px; font-size: 1.375rem; font-weight: 800; letter-spacing: -0.5px; color: var(--tx); }
  .gate__btn { display: inline-block; margin-top: 18px; padding: 9px 18px; font-size: 0.7812rem; }
  .gate__link { display: block; margin: 12px auto 0; font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .who { display: inline-flex; align-items: center; gap: 6px; font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .signout { padding: 6px; border-radius: var(--r-control); color: var(--mut); }
  .signout:hover { color: var(--acc); }
</style>
